import axios from "axios";
import AuthService from "../utils/AuthService";

const normalizeBaseUrl = () => {
  const rawUrl = process.env.REACT_APP_ITEMS_API_URL;

  if (typeof rawUrl !== "string") {
    return undefined;
  }

  const trimmedUrl = rawUrl.trim();

  if (!trimmedUrl || ["undefined", "null"].includes(trimmedUrl.toLowerCase())) {
    return undefined;
  }

  return trimmedUrl.replace(/\/$/, "");
};

const getBaseUrl = () => {
  const baseUrl = normalizeBaseUrl();

  if (!baseUrl) {
    throw new Error("Items API URL is not configured.");
  }

  return baseUrl;
};

const buildItemsUrl = (type) => {
  const baseUrl = getBaseUrl();
  const query = type ? `?type=${encodeURIComponent(type)}` : "";

  return `${baseUrl}/items${query}`;
};

const fetchItems = async (type, nextToken) => {
  const baseUrl = getBaseUrl();

  let url = `${baseUrl}/items?type=${encodeURIComponent(type)}`;

  if (nextToken) {
    url += `&nextToken=${encodeURIComponent(nextToken)}`;
  }

  const response = await axios.get(url, AuthService.getAuthHeader());
  return response.data; // items + nextToken
};

const fetchItemById = async (id) => {
  if (!id) {
    throw new Error("An item id is required to fetch an item.");
  }

  const baseUrl = getBaseUrl();
  const url = `${baseUrl}/item/${encodeURIComponent(id)}`;
  const response = await axios.get(url, AuthService.getAuthHeader());

  return response.data;
};

const saveItem = async (payload, id) => {
  const baseUrl = getBaseUrl();
  if (id) {
    const response = await axios.put(
      `${baseUrl}/item/${encodeURIComponent(id)}`,
      payload,
      AuthService.getAuthHeader()
    );

    return response.data;
  }

  const response = await axios.post(
    `${baseUrl}/item`,
    payload,
    AuthService.getAuthHeader()
  );

  return response.data;
};

const buildTaxonomyUrl = (id, type) => {
  const baseUrl = getBaseUrl();

  const path = id
    ? `${baseUrl}/taxonomy/${encodeURIComponent(id)}`
    : `${baseUrl}/taxonomy`;
  const query = type ? `?type=${encodeURIComponent(type)}` : "";

  return `${path}${query}`;
};

const fetchTaxonomy = async (type) => {
  const baseUrl = getBaseUrl();
  const query = type ? `?type=${encodeURIComponent(type)}` : "";
  const url = `${baseUrl}/taxonomy${query}`;
  const response = await axios.get(url, AuthService.getAuthHeader());
  const { data } = response;

  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.items)) {
    return data.items;
  }

  return [];
};

const createTaxonomy = async (payload) => {
  const url = buildTaxonomyUrl();
  const response = await axios.post(url, payload, AuthService.getAuthHeader());

  return response.data;
};

const updateTaxonomy = async (id, payload) => {
  if (!id) {
    throw new Error("An id is required to update a taxonomy entry.");
  }

  const url = buildTaxonomyUrl(id);
  const response = await axios.put(url, payload, AuthService.getAuthHeader());

  return response.data;
};

const deleteTaxonomy = async (id, type) => {
  if (!id) {
    throw new Error("An id is required to delete a taxonomy entry.");
  }

  const url = buildTaxonomyUrl(id, type);
  const response = await axios.delete(url, AuthService.getAuthHeader());

  return response.data;
};

const ItemService = {
  fetchItems,
  fetchItemById,
  saveItem,
  fetchTaxonomy,
  createTaxonomy,
  updateTaxonomy,
  deleteTaxonomy,
  isConfigured: () => Boolean(normalizeBaseUrl()),
};

export default ItemService;
